import React, { useState, useEffect } from 'react';
import { earningApi } from '../api/services';
import Loader from '../components/Loader';
import { Wallet, Search, X, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/Customers.css';

const AstrologerEarnings = () => {
  const [data, setData] = useState([]);
  const [astrologers, setAstrologers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterAstrologerId, setFilterAstrologerId] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  // Picked dates vs. the period actually shown: the table only changes on
  // Apply (or a quick period button), never half-way through picking dates.
  // No dates = all time.
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [period, setPeriod] = useState({ from: null, to: null });
  const [summary, setSummary] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page };
      if (filterAstrologerId) params.astrologerId = filterAstrologerId;
      if (appliedSearch) params.searchString = appliedSearch;
      if (period.from) params.from_date = moment(period.from).format('YYYY-MM-DD');
      if (period.to) params.to_date = moment(period.to).format('YYYY-MM-DD');
      const res = await earningApi.astrologerEarning(params);
      const d = res.data.data || res.data;
      setData(d.partnerWiseEarning || []);
      setAstrologers(d.astrologers || []);
      setTotalPages(d.totalPages || 1);
      setTotalRecords(d.totalRecords || 0);
      setSummary(d.summary || null);
    } catch (err) {
      console.error('Error fetching astrologer earnings:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [page, filterAstrologerId, appliedSearch, period]);

  const handleSearch = () => { setPage(1); setAppliedSearch(search.trim()); };

  const applyPeriod = (from, to) => {
    setFromDate(from);
    setToDate(to);
    setPeriod({ from, to });
    setPage(1);
  };

  const handleDateSubmit = () => applyPeriod(fromDate, toDate);
  const thisMonth = () => applyPeriod(moment().startOf('month').toDate(), moment().toDate());
  const lastMonth = () => {
    const m = moment().subtract(1, 'month');
    applyPeriod(m.clone().startOf('month').toDate(), m.clone().endOf('month').toDate());
  };

  const clearFilters = () => {
    setSearch('');
    setAppliedSearch('');
    setFilterAstrologerId('');
    applyPeriod(null, null);
  };

  const periodApplied = !!(period.from || period.to);
  const periodLabel = !periodApplied
    ? 'All time'
    : `${period.from ? moment(period.from).format('DD MMM YYYY') : 'Start'} – ${period.to ? moment(period.to).format('DD MMM YYYY') : 'Today'}`;

  const fmt = (val) => val != null ? `₹${Number(val).toFixed(2)}` : '₹0.00';

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    if (start > 1) { pages.push(1); if (start > 2) pages.push('dots-start'); }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) { if (end < totalPages - 1) pages.push('dots-end'); pages.push(totalPages); }
    return (
      <div className="cust-pagination">
        <span className="cust-page-info">
          Page {page} of {totalPages} ({totalRecords} total)
        </span>
        <div className="cust-page-btns">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="cust-page-btn">
            <ChevronLeft size={14} />
          </button>
          {pages.map((p, idx) =>
            typeof p === 'string' ? (
              <span key={p} className="cust-page-dots">...</span>
            ) : (
              <button key={p} onClick={() => setPage(p)} className={`cust-page-btn ${p === page ? 'active' : ''}`}>
                {p}
              </button>
            )
          )}
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="cust-page-btn">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Top Bar */}
      <div className="cust-topbar">
        <div className="cust-topbar-left">
          <Wallet size={25} color="#7c3aed" />
          <div>
            <h2 className="cust-title">Astrologer Earnings</h2>
            <div className="cust-count">
              {periodLabel} · {totalRecords} astrologers · Earned {fmt(summary?.totalEarning)}
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="cust-filterbar">
        <div className="cust-filter-group cust-filter-search-group">
          <label className="cust-filter-label">Search</label>
          <div className="cust-filter-search">
            <Search size={14} className="cust-search-icon" />
            <input
              type="text"
              placeholder="Search astrologer..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
              className="cust-input cust-search-input"
            />
            {search && (
              <button onClick={() => { setSearch(''); setAppliedSearch(''); setPage(1); }} className="cust-search-clear">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        <div className="cust-filter-group">
          <label className="cust-filter-label">Astrologer</label>
          <select
            value={filterAstrologerId}
            onChange={e => { setFilterAstrologerId(e.target.value); setPage(1); }}
            className="cust-input"
          >
            <option value="">All Astrologers</option>
            {astrologers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <div className="cust-filter-date-row">
          <div className="cust-filter-group">
            <label className="cust-filter-label">From</label>
            <div className="cust-datepicker-wrap">
              <Calendar size={14} className="cust-datepicker-icon" />
              <DatePicker
                selected={fromDate}
                onChange={date => setFromDate(date)}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                maxDate={toDate || undefined}
                dateFormat="dd MMM yyyy"
                className="cust-input cust-datepicker-input"
                placeholderText="All time"
                isClearable
              />
            </div>
          </div>
          <div className="cust-filter-group">
            <label className="cust-filter-label">To</label>
            <div className="cust-datepicker-wrap">
              <Calendar size={14} className="cust-datepicker-icon" />
              <DatePicker
                selected={toDate}
                onChange={date => setToDate(date)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate || undefined}
                dateFormat="dd MMM yyyy"
                className="cust-input cust-datepicker-input"
                placeholderText="Today"
                isClearable
              />
            </div>
          </div>
          <div className="cust-filter-actions">
            <button onClick={handleDateSubmit} className="cust-btn cust-btn-primary">
              <Search size={13} /> Apply
            </button>
            <button onClick={thisMonth} className="cust-btn cust-btn-ghost">This Month</button>
            <button onClick={lastMonth} className="cust-btn cust-btn-ghost">Last Month</button>
            {(appliedSearch || periodApplied || filterAstrologerId) && (
              <button onClick={clearFilters} className="cust-btn cust-btn-danger">
                <X size={13} /> Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Card + Table */}
      <div className="cust-card">
        {loading ? <Loader text="Loading astrologer earnings..." /> : (
          <div className="cust-table-wrap">
            <table className="cust-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Astrologer</th>
                  <th>Chat Earning</th>
                  <th>Call Earning</th>
                  <th>Report Earning</th>
                  <th>Gift Earning</th>
                  <th>Total Earning</th>
                  <th>Withdrawn</th>
                  <th>Wallet Balance (Now)</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? data.map((row, i) => (
                  <tr key={row.astrologerId || i}>
                    <td>{(page - 1) * 15 + i + 1}</td>
                    <td className="cust-name-cell">{row.astrologerName || '-'}</td>
                    <td>{fmt(row.chatEarning)}</td>
                    <td>{fmt(row.callEarning)}</td>
                    <td>{fmt(row.reportEarning)}</td>
                    <td>{fmt(row.giftEarning)}</td>
                    <td style={{ fontWeight: 700 }}>{fmt(row.totalEarning)}</td>
                    <td>
                      <span className="cust-verify-badge unverified">
                        {fmt(row.totalWithdrawal)}
                      </span>
                    </td>
                    <td>
                      <span className="cust-verify-badge verified">
                        {fmt(row.totalbalance)}
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={9} className="cust-no-data">No astrologer earnings for this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {renderPagination()}
      </div>
    </div>
  );
};

export default AstrologerEarnings;
